<template>
  <section id="features" class="section-padding bg-white">
    <div class="container-custom">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <div class="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-6">
          <i class="fas fa-rocket mr-2"></i>
          Enterprise-Grade Features
        </div>
        <h2 class="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
          Transform Your WordPress Into a
          <span class="gradient-text">Professional Project Management Hub</span>
        </h2>
        <p class="text-xl text-gray-600 max-w-4xl mx-auto">
          Stop juggling multiple tools and spreadsheets. Task Manager Pro brings enterprise-level project management 
          directly into your WordPress site, saving you thousands in monthly SaaS subscriptions while providing 
          superior functionality tailored to your business needs.
        </p>
      </div>
      
      <!-- Features Grid -->
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        <!-- Feature 1: Kanban Boards -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-columns text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Visual Kanban Boards</h3>
          <p class="text-gray-600 mb-4">
            <strong>Revolutionize your workflow</strong> with stunning, drag-and-drop Kanban boards that make project 
            management intuitive and engaging. Watch your team's productivity soar as tasks flow seamlessly from 
            "To Do" to "Done" with beautiful, customizable boards.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Drag & Drop Magic:</strong> Move tasks between columns effortlessly
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Custom Columns:</strong> Create unlimited custom workflow stages
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Auto-Sync:</strong> Changes sync across all devices
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Color Coding:</strong> Visual priority and status indicators
            </li>
          </ul>
          <div class="mt-4 p-3 bg-green-50 rounded-lg">
            <p class="text-sm text-green-800 font-medium">
              💡 <strong>Pro Tip:</strong> Visual boards make it easy to spot bottlenecks and keep projects moving!
            </p>
          </div>
        </div>
        
        <!-- Feature 2: Advanced Workflows -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-project-diagram text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Intelligent Automation</h3>
          <p class="text-gray-600 mb-4">
            <strong>Eliminate repetitive tasks</strong> with powerful workflow automation that works 24/7. 
            Create smart rules that automatically assign tasks, send notifications, update statuses, and 
            trigger actions based on your business logic.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Smart Triggers:</strong> 15+ trigger conditions (deadline, status, assignee)
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Email Automation:</strong> Smart notifications to stakeholders
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Workflow Rules:</strong> Create automated workflows with triggers and actions
            </li>
          </ul>
          <div class="mt-4 p-3 bg-purple-50 rounded-lg">
            <p class="text-sm text-purple-800 font-medium">
              ⚡ <strong>Save Time:</strong> Automate up to 80% of routine project management tasks!
            </p>
          </div>
        </div>
        
        <!-- Feature 3: Team Collaboration -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-users text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Seamless Team Collaboration</h3>
          <p class="text-gray-600 mb-4">
            <strong>Bring your team together</strong> with powerful collaboration features that eliminate 
            communication gaps. Share files, discuss tasks, mention team members, and keep everyone 
            synchronized without leaving WordPress.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Task Updates:</strong> Stay informed with task and project updates
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>File Sharing:</strong> Upload and share documents securely
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Activity Feed:</strong> Complete audit trail of all actions
            </li>
          </ul>
          <div class="mt-4 p-3 bg-green-50 rounded-lg">
            <p class="text-sm text-green-800 font-medium">
              🤝 <strong>Result:</strong> Keep every conversation and decision attached to the task it belongs to!
            </p>
          </div>
        </div>
        
        <!-- Feature 4: Time Tracking -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-clock text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Precision Time Tracking</h3>
          <p class="text-gray-600 mb-4">
            <strong>Know exactly where time goes</strong> with our comprehensive time tracking system. 
            Track billable hours, analyze productivity patterns, and generate detailed reports that 
            help you price projects accurately and improve team efficiency.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>One-Click Timer:</strong> Start/stop tracking with a single click
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Client Reports:</strong> Professional time reports for clients
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Mobile Responsive:</strong> Track time from any device with responsive design
            </li>
          </ul>
          <div class="mt-4 p-3 bg-orange-50 rounded-lg">
            <p class="text-sm text-orange-800 font-medium">
              💰 <strong>ROI:</strong> Accurate time logs mean accurate invoices and fewer underpriced projects!
            </p>
          </div>
        </div>
        
        <!-- Feature 5: Advanced Permissions -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-shield-alt text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">
            Enterprise Security
            <span class="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">NEW: 80% Faster</span>
          </h3>
          <p class="text-gray-600 mb-4">
            <strong>Protect sensitive project data</strong> with granular permission controls that give you 
            complete control over who sees what. Perfect for agencies managing multiple clients or 
            enterprises with strict compliance requirements. <strong>Now with lightning-fast permission caching!</strong>
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Permission Caching:</strong> 80% faster permission checks with intelligent caching
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Follower/Watcher System:</strong> Follow tasks like Asana, Trello, and ClickUp
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Client Isolation:</strong> Complete data separation between clients
            </li>
          </ul>
          <div class="mt-4 p-3 bg-red-50 rounded-lg">
            <p class="text-sm text-red-800 font-medium">
              🔒 <strong>Latest Update:</strong> Industry-leading performance with permission caching and follower system!
            </p>
          </div>
        </div>
        
        <!-- Feature 6: Analytics & Reports -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-chart-bar text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Business Intelligence</h3>
          <p class="text-gray-600 mb-4">
            <strong>Make data-driven decisions</strong> with comprehensive analytics and beautiful reports. 
            Track project ROI, team performance, client satisfaction, and business metrics that matter 
            to your bottom line.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Project Dashboards:</strong> Monitor project health and progress
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Performance Metrics:</strong> Track velocity, efficiency, and quality
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Client Reports:</strong> Professional reports for stakeholders
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Export Options:</strong> CSV export for data analysis
            </li>
          </ul>
          <div class="mt-4 p-3 bg-indigo-50 rounded-lg">
            <p class="text-sm text-indigo-800 font-medium">
              📊 <strong>Insight:</strong> Turn raw task data into reports your clients and stakeholders actually read!
            </p>
          </div>
        </div>
        
        <!-- Feature 7: Public Visibility -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-globe text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">
            Public Visibility & Sharing
            <span class="ml-2 px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">NEW</span>
          </h3>
          <p class="text-gray-600 mb-4">
            <strong>Share projects and tasks publicly</strong> with powerful shortcodes and public URLs. 
            Create client portals, public project showcases, and team dashboards that can be embedded 
            anywhere on your website without requiring user authentication.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Public Task URLs:</strong> Shareable links with custom slugs (e.g., /task/demo-task)
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Shortcode Support:</strong> Board view, list view, single task, project overview
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Client Portals:</strong> Create beautiful client-facing dashboards
            </li>
          </ul>
          <div class="mt-4 p-3 bg-teal-50 rounded-lg">
            <p class="text-sm text-teal-800 font-medium">
              🌐 <strong>Latest:</strong> Public visibility system now supports client portals and public project showcases!
            </p>
          </div>
        </div>
        
        <!-- Feature 8: Modern Interface -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-paint-brush text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">Modern User Interface</h3>
          <p class="text-gray-600 mb-4">
            <strong>Beautiful, intuitive interface</strong> designed with Tailwind CSS for a modern, 
            professional look. Reorganized menu structure makes navigation effortless and helps you focus 
            on what matters - getting work done.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>5 Logical Menus:</strong> Dashboard, Work, Team, Support, Settings
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Responsive Design:</strong> Perfect on desktop, tablet, and mobile
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Quick Access:</strong> Boards moved to first position in Tasks menu
            </li>
          </ul>
          <div class="mt-4 p-3 bg-cyan-50 rounded-lg">
            <p class="text-sm text-cyan-800 font-medium">
              🎨 <strong>Result:</strong> Users find what they need 40% faster with the reorganized menu structure!
            </p>
          </div>
        </div>
        
        <!-- Feature 9: Support Ticket System -->
        <div class="feature-card group">
          <div class="w-12 h-12 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
            <i class="fas fa-ticket-alt text-white text-xl"></i>
          </div>
          <h3 class="text-xl font-bold text-gray-900 mb-4">
            Integrated Support Ticket System
            <span class="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">UNIQUE</span>
          </h3>
          <p class="text-gray-600 mb-4">
            <strong>Manage client support directly in WordPress</strong> with our integrated ticket system. 
            Perfect for agencies who need to track client requests, assign support tickets to team members, 
            and maintain communication history - all without leaving your WordPress admin.
          </p>
          <ul class="text-sm text-gray-500 space-y-2">
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Complete Ticket Management:</strong> Create, assign, and track support tickets
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Priority & Type System:</strong> Organize tickets by priority and type
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>CSV Export:</strong> Export tickets for reporting and analysis
            </li>
            <li class="flex items-center">
              <i class="fas fa-check text-green-500 mr-2"></i>
              <strong>Team Assignment:</strong> Assign tickets to specific team members
            </li>
          </ul>
          <div class="mt-4 p-3 bg-yellow-50 rounded-lg">
            <p class="text-sm text-yellow-800 font-medium">
              🎫 <strong>Perfect for Agencies:</strong> Manage all client support within your WordPress site - no external tools needed!
            </p>
          </div>
        </div>
      </div>
      
      <!-- Additional Features -->
      <div class="mt-16 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 text-center">Plus These Game-Changing Features</h3>
        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div class="text-center bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-mobile-alt text-white text-2xl"></i>
            </div>
            <h4 class="font-semibold text-gray-900 mb-2">Mobile-First Design</h4>
            <p class="text-sm text-gray-600">Perfect experience on phones, tablets, and desktops. Your team can work from anywhere!</p>
          </div>
          <div class="text-center bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-code text-white text-2xl"></i>
            </div>
            <h4 class="font-semibold text-gray-900 mb-2">
              Smart Shortcodes
              <span class="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">NEW</span>
            </h4>
            <p class="text-sm text-gray-600">Display project dashboards, public boards, task lists, and team directories anywhere on your site! Now with public visibility support and shareable public URLs.</p>
          </div>
          <div class="text-center bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-headset text-white text-2xl"></i>
            </div>
            <h4 class="font-semibold text-gray-900 mb-2">Premium Support</h4>
            <p class="text-sm text-gray-600">24/7 expert support, video tutorials, and dedicated onboarding assistance!</p>
          </div>
          <div class="text-center bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div class="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg flex items-center justify-center mx-auto mb-4">
              <i class="fas fa-file-export text-white text-2xl"></i>
            </div>
            <h4 class="font-semibold text-gray-900 mb-2">Import/Export</h4>
            <p class="text-sm text-gray-600">Easy data migration from other tools. Export your projects and tasks in CSV format!</p>
          </div>
        </div>


      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FeaturesSection'
}
</script>
